import React, { useState, useEffect, useRef } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const DEFAULT_CHART_HEIGHT = 180;
const MIN_CHART_HEIGHT = 56;

// Wrapper that measures container and passes explicit dimensions - more reliable than ResponsiveContainer in flex/grid layouts
const ChartWrapper = ({ children }) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 300, height: DEFAULT_CHART_HEIGHT });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      const w = cr.width;
      const h = cr.height;
      setSize((prev) => ({
        width: w > 0 ? Math.floor(w) : prev.width,
        height: h > 0 ? Math.max(MIN_CHART_HEIGHT, Math.floor(h)) : prev.height,
      }));
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    setSize({
      width: rect.width > 0 ? Math.floor(rect.width) : 300,
      height:
        rect.height > 0
          ? Math.max(MIN_CHART_HEIGHT, Math.floor(rect.height))
          : DEFAULT_CHART_HEIGHT,
    });
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", minHeight: MIN_CHART_HEIGHT }}
    >
      {size.width > 0 && size.height > 0 && children(size.width, size.height)}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: 600, color: "#111827" }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p
            key={index}
            style={{
              margin: "4px 0",
              color: entry.color,
              fontSize: "14px",
            }}
          >
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const ChartContainer = ({ children, className = "" }) => {
  return (
    <div
      className={className}
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {children}
    </div>
  );
};

export const ChartConfig = {
  totalMessages: {
    label: "Messages",
    color: "hsl(188, 70%, 50%)",
  },
  sessionsToday: {
    label: "Sessions",
    color: "hsl(262, 83%, 58%)",
  },
  activeSessions: {
    label: "Active",
    color: "hsl(142, 76%, 36%)",
  },
  totalUsers: {
    label: "Users",
    color: "hsl(38, 92%, 50%)",
  },
  totalFeedback: {
    label: "Feedback",
    color: "hsl(221, 83%, 53%)",
  },
  totalSessions: {
    label: "Sessions",
    color: "hsl(0, 84%, 60%)",
  },
};

export const AreaChartComponent = ({ data, dataKey, color }) => {
  const gradientId = `gradient-${(color || "").replace(/#/g, "")}-${Math.random().toString(36).substr(2, 9)}`;
  return (
    <ChartWrapper>
      {(w, h) => (
        <AreaChart width={w} height={h} data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.8} />
              <stop offset="95%" stopColor={color} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} />
          <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#${gradientId})`} strokeWidth={2} />
        </AreaChart>
      )}
    </ChartWrapper>
  );
};

export const BarChartComponent = ({ data, dataKey, color }) => {
  return (
    <ChartWrapper>
      {(w, h) => (
        <BarChart width={w} height={h} data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} />
          <Bar dataKey={dataKey} fill={color} radius={[8, 8, 0, 0]} />
        </BarChart>
      )}
    </ChartWrapper>
  );
};

export const LineChartComponent = ({ data, dataKey, color }) => {
  return (
    <ChartWrapper>
      {(w, h) => (
        <LineChart width={w} height={h} data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ fill: color, r: 4 }} activeDot={{ r: 6 }} />
        </LineChart>
      )}
    </ChartWrapper>
  );
};

